import { useState } from "react";
import { toast } from "react-toastify";

import { ApiError } from "@/models/apiError";
import {
  WhatsAppIntegrationFormData,
  WhatsAppRouting,
  WhatsAppTransportType,
  UazapiReconciliationStatus,
} from "@/models/whatsAppIntegration";
import api from "@/shared/api";
import { API_ERRORS, ENDPOINTS, UTILS } from "@/shared/constants";
import { updateLoggedUser } from '@/store/features/authSlice'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import useRouting from "./useRouting";

export default function useWhatssetWhatsAppIntegrationsApi() {
  const { getRouteWithId } = useRouting()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const [cellPhone, setCellPhone] = useState(UTILS.BLANK)
  const [routing, setRouting] = useState<WhatsAppRouting | null>(null)
  const [isRoutingLoading, setIsRoutingLoading] = useState(false)
  const [reconciliationStatus, setReconciliationStatus] =
    useState<UazapiReconciliationStatus | null>(null)
  const [isReconciliationStatusLoading, setIsReconciliationStatusLoading] =
    useState(false)

  async function createWhatsAppIntegration(data: WhatsAppIntegrationFormData) {
    try {
      await api.post(ENDPOINTS.WHATSAPP_INTEGRATIONS.POST.CREATE, parseData(data))
      toast.success('Credenciais criadas')
      return true
    } catch (error) {
      const apiError = error as ApiError;
      handleSaveError(apiError.response?.data?.errorCode);
      return false;
    }
  }

  async function updateWhatsAppIntegration(id: string, data: WhatsAppIntegrationFormData) {
    try {
      await api.patch(getRouteWithId(ENDPOINTS.WHATSAPP_INTEGRATIONS.PATCH.BY_ID, id), parseData(data))
      toast.success('Credenciais atualizadas')
      return true
    } catch (error) {
      const apiError = error as ApiError;
      handleSaveError(apiError.response?.data?.errorCode);
      return false;
    }
  }

  async function updateWhatsAppIntegrationNumber(id: string, cellPhone: string) {
    try {
      await api.patch(getRouteWithId(ENDPOINTS.WHATSAPP_INTEGRATIONS.PATCH.CELL_PHONE_BY_ID, id), { cellPhone })
      toast.success('Número atualizado')
      return true
    } catch (error) {
      const apiError = error as ApiError;
      handleSaveError(apiError.response?.data?.errorCode);
      return false;
    }
  }

  async function getWhatsAppIntegrationNumber(id: string) {
    try {
      const response = await api.get(getRouteWithId(ENDPOINTS.WHATSAPP_INTEGRATIONS.GET.CELL_PHONE_BY_ID, id),)
      setCellPhone(response.data.cellPhone)
    } catch {
      toast.error('Erro ao obter número')
      setCellPhone(UTILS.BLANK)
    }
  }

  async function unlinkOfficialTransport(id: string) {
    try {
      await api.delete(
        getRouteWithId(
          ENDPOINTS.WHATSAPP_INTEGRATIONS.DELETE.OFFICIAL_TRANSPORT_BY_ID,
          id
        )
      )
      toast.success('ChatSC desconectado da Meta')
      return true
    } catch (error) {
      const apiError = error as ApiError
      const message = apiError.response?.data?.message
      if (message) {
        toast.error(message)
      } else {
        handleSaveError(apiError.response?.data?.errorCode)
      }
      return false
    }
  }

  async function getWhatsAppRouting() {
    setIsRoutingLoading(true)
    try {
      const response = await api.get<WhatsAppRouting>(
        ENDPOINTS.WHATSAPP_INTEGRATIONS.GET.ROUTING
      )
      setRouting(response.data)
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      handleSaveError(apiError.response?.data?.errorCode)
      return null
    } finally {
      setIsRoutingLoading(false)
    }
  }

  async function getUazapiReconciliationStatus(id: string) {
    setIsReconciliationStatusLoading(true)
    try {
      const response = await api.get<UazapiReconciliationStatus>(
        getRouteWithId(
          ENDPOINTS.WHATSAPP_INTEGRATIONS.GET.RECONCILIATION_STATUS_BY_ID,
          id
        )
      )
      setReconciliationStatus(response.data)
      return response.data
    } catch {
      setReconciliationStatus(null)
      return null
    } finally {
      setIsReconciliationStatusLoading(false)
    }
  }

  async function updateWhatsAppRouting(
    id: number,
    directTransport: WhatsAppTransportType
  ) {
    setIsRoutingLoading(true)
    try {
      const response = await api.patch<WhatsAppRouting>(
        getRouteWithId(
          ENDPOINTS.WHATSAPP_INTEGRATIONS.PATCH.ROUTING_BY_ID,
          id.toString()
        ),
        { directTransport }
      )
      setRouting(response.data)
      if (user?.company?.whatsappIntegration?.id === id) {
        dispatch(
          updateLoggedUser({
            ...user,
            company: {
              ...user.company,
              whatsappIntegration: {
                ...user.company.whatsappIntegration,
                directTransport: response.data.directTransport,
                broadcastTransport: response.data.broadcastTransport,
              },
            },
          })
        )
      }
      toast.success('Roteamento do WhatsApp atualizado')
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      handleSaveError(apiError.response?.data?.errorCode)
      return null
    } finally {
      setIsRoutingLoading(false)
    }
  }

  function parseData(data: WhatsAppIntegrationFormData) {
    const resolveSecret = (value: string) =>
      value === '••••••••' ? UTILS.BLANK : value
    const unofficial = data.unofficialProvider === 'uazapi'
      ? {
          provider: 'uazapi' as const,
          config: {
        baseUrl: data.baseUrl,
        instance: data.instance,
        token: resolveSecret(data.token),
          },
        }
      : {
          provider: 'z-api' as const,
          config: {
        instanceId: data.instanceId,
        instanceToken: resolveSecret(data.instanceToken),
        clientToken: resolveSecret(data.clientToken),
          },
        }

    return {
      companyId: data.companyId,
      cellPhone: data.cellPhone,
      unofficial,
      directTransport: data.directTransport,
      broadcastTransport: data.broadcastTransport,
    }
  }

  function handleSaveError(errorCode?: number) {
    if (errorCode && API_ERRORS[errorCode]) {
      toast.error(API_ERRORS[errorCode]);
    } else {
      toast.error('Erro ao salvar credenciais');
    }
  }

  return {
    createWhatsAppIntegration,
    updateWhatsAppIntegration,
    updateWhatsAppIntegrationNumber,
    getWhatsAppIntegrationNumber,
    unlinkOfficialTransport,
    getWhatsAppRouting,
    updateWhatsAppRouting,
    getUazapiReconciliationStatus,
    routing,
    isRoutingLoading,
    reconciliationStatus,
    isReconciliationStatusLoading,
    cellPhone
  }
}
